import { AxiosResponse } from "axios";
import { APP_ROUTES, CONTENT_QUALITY_LEVELS, ENDPOINTS } from "const";
import { FormikHelpers } from "formik";
import {
  useActionGroup,
  useActionGroupSkills,
  useMe,
  useSolution,
  useUpdateAttachments,
} from "hooks";
import { useCallback, useMemo } from "react";
import { useMutation, UseMutationOptions, useQueryClient } from "react-query";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  FullActionGroupType,
  IActionGroupSkill,
  IAISimilar,
  IAISuggestions,
  IAsset,
  IAttachment,
  IFeedbackArea,
} from "types";
import { showErrorMessage, showSuccessMessage } from "utils";
import { ActionGroupQualityReviewMap, IManagementProps } from "./GroupForm.types";
import {
  createActionGroup,
  createActionGroupSkills,
  updateActionGroup,
  updateActionGroupSkills,
} from "./GroupForm.services";
import { ActionGroupPayload, FIELD_NAMES, InitActionGroup } from "./GroupForm.constants";
import {
  createAISimilarGroups,
  createAISuggestionsGroups,
  IAIGroupsProps,
} from "services";

type ActionGroupFormValues = Partial<InitActionGroup> & {
  suggestedResult?: IAISuggestions;
};

export function useInitialState() {
  const params = useParams<{ id: string }>();
  const { data: me } = useMe();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isOnActionGroupPage = location.pathname.includes(
    APP_ROUTES.ACTION_GROUP
  );
  const editGroupId = isOnActionGroupPage
    ? params.id
    : searchParams.get("groupId") || "";
  const { data: actionGroup } = useActionGroup({
    id: editGroupId,
    enabled: !!editGroupId,
    user: me?.data.id,
  });
  const { data: groupSkills } = useActionGroupSkills({
    id: actionGroup?.data.id,
    enabled: !!actionGroup,
  });

  return useMemo(() => {
    const init = new InitActionGroup(actionGroup?.data, groupSkills?.data);
    return init;
  }, [groupSkills?.data, actionGroup?.data]);
}

interface IAIAnalyticsGroupsProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISuggestions>, any, any>> {
  id?: string | undefined;
}

export function useCreateAIAnalyticsGroups({
  ...rest
}: IAIAnalyticsGroupsProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISuggestions>, any, any>(
    (group: IAIGroupsProps) => createAISuggestionsGroups({ ...group }),
    {
      mutationKey: ["createAIAnalyticsGroups"],
      onSuccess: (data) => {
        queryClient.setQueryData(["createAIAnalyticsGroups"], data.data || {});
      },
      ...rest,
    }
  );
}

interface IAISimilarGroupProps
  extends Partial<UseMutationOptions<AxiosResponse<IAISimilar>, any, any>> {
  id?: string | undefined;
}

export function useCreateAISimilarGroup({ ...rest }: IAISimilarGroupProps) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<IAISimilar>, any, any>(
    (group: IAIGroupsProps) => createAISimilarGroups({ ...group }),
    {
      mutationKey: ["createAISimilarGroup"],
      onSuccess: (data) => {
        queryClient.setQueryData(["createAISimilarGroup"], data.data || {});
      },

      ...rest,
    }
  );
}

interface IActionGroupsProps
  extends Partial<
    UseMutationOptions<AxiosResponse<FullActionGroupType>, any, any>
  > {
  id?: string | undefined;
}

interface IActionGroupsSkillsProps
  extends Partial<
    UseMutationOptions<AxiosResponse<IActionGroupSkill[]>, any, any>
  > {}

export function useCreateActionGroup({ ...rest }: IActionGroupsProps) {
  return useMutation<AxiosResponse<FullActionGroupType>, any, any>(
    (group: FullActionGroupType) => createActionGroup({ group }),
    {
      ...rest,
    }
  );
}

export function useUpdateActionGroup({
  id = "",
  ...rest
}: IActionGroupsProps & {
  id: string | undefined;
}) {
  return useMutation<AxiosResponse<FullActionGroupType>, any, any>(
    (group: FullActionGroupType) => updateActionGroup({ id, group }),
    {
      ...rest,
    }
  );
}

export function useCreateActionGroupSkill({
  ...rest
}: IActionGroupsSkillsProps) {
  return useMutation<AxiosResponse<IActionGroupSkill[]>, any, any>(
    (groupSkill: IActionGroupSkill[]) =>
      createActionGroupSkills({ groupSkill }),
    {
      ...rest,
    }
  );
}

export function useUpdateActionGroupSkill({
  ...rest
}: IActionGroupsSkillsProps) {
  return useMutation<AxiosResponse<IActionGroupSkill[]>, any, any>(
    (groupSkill: IActionGroupSkill[]) =>
      updateActionGroupSkills({ groupSkill }),
    {
      ...rest,
    }
  );
}

export function useSubmitHandler(
  success: () => void,
  manage: IManagementProps
) {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const isOnActionGroupPage = location.pathname.includes(
    APP_ROUTES.ACTION_GROUP
  );
  const { data: me } = useMe();
  const [searchParams] = useSearchParams();
  const editGroupId = isOnActionGroupPage
    ? params.id
    : searchParams.get("groupId") || "";
  const solutionId = isOnActionGroupPage ? undefined : params.id;
  const { mutateAsync: mutateCreateActionGroupAsync } = useCreateActionGroup(
    {}
  );
  const { mutateAsync: mutateUpdateActionGroupAsync } = useUpdateActionGroup({
    id: editGroupId,
  });

  const { data: solutionData } = useSolution({
    id: solutionId,
    enabled: !!solutionId,
  });

  const { mutateAsync: mutateCreateAISuggestionGroupAsync } =
    useCreateAIAnalyticsGroups({});
  const { mutateAsync: mutateCreateAISimilarGroupAsync } =
    useCreateAISimilarGroup({});

  const { mutateAsync: mutateCreateActionGroupSkillAsync } =
    useCreateActionGroupSkill({});
  const { mutateAsync: mutateUpdateActionGroupSkillAsync } =
    useUpdateActionGroupSkill({});

  const { data: actionGroupForEdit } = useActionGroup({
    id: editGroupId,
    enabled: !!editGroupId,
    user: me?.data.id,
  });

  const queryClient = useQueryClient();
  const { data: meData } = useMe({});
  const { mutateAsync: updateAttachments } = useUpdateAttachments();

  const qualityTrackedFields = useMemo(
    () => [
      FIELD_NAMES.TITLE,
      FIELD_NAMES.DESCRIPTION,
      FIELD_NAMES.SCHEDULE,
      FIELD_NAMES.CONTACT,
    ],
    []
  );
  const qualityTrackedFieldSet = useMemo(
    () =>
      new Set(
        qualityTrackedFields.map((field) => field.toLocaleLowerCase())
      ),
    [qualityTrackedFields]
  );

  const normaliseAiInputName = (name: string | undefined | null) => {
    const lower = (name || "").toLocaleLowerCase();
    if (lower === "goals") {
      return FIELD_NAMES.DESCRIPTION.toLocaleLowerCase();
    }
    return lower;
  };

  const normalize = useCallback((value?: string | null) => {
    if (!value) {
      return "";
    }
    const unicodeNormalized =
      typeof value.normalize === "function" ? value.normalize("NFKC") : value;
    return unicodeNormalized
      .trim()
      .replace(/\s+/g, " ")
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      .replace(/[\u2013\u2014\u2212]/g, "-")
      .replace(/([!?.,])\1+/g, "$1")
      .replace(/[!?.,]+$/g, "")
      .toLocaleLowerCase();
  }, []);

  const levenshteinDistance = useCallback((a: string, b: string) => {
    if (a === b) {
      return 0;
    }
    if (!a.length) {
      return b.length;
    }
    if (!b.length) {
      return a.length;
    }

    const lenB = b.length;
    const prev = Array.from({ length: lenB + 1 }, (_, index) => index);
    const curr = new Array<number>(lenB + 1);

    for (let i = 1; i <= a.length; i++) {
      curr[0] = i;
      const charA = a[i - 1];
      for (let j = 1; j <= lenB; j++) {
        const cost = charA === b[j - 1] ? 0 : 1;
        const insertion = curr[j - 1] + 1;
        const deletion = prev[j] + 1;
        const substitution = prev[j - 1] + cost;
        curr[j] = Math.min(insertion, deletion, substitution);
      }
      for (let j = 0; j <= lenB; j++) {
        prev[j] = curr[j];
      }
    }
    return prev[lenB];
  }, []);

  const isMinorEdit = useCallback(
    (previous: string, current: string) => {
      if (!previous || !current) {
        return false;
      }
      const maxEditDistance = 3;
      if (Math.abs(previous.length - current.length) > maxEditDistance) {
        return false;
      }
      const distance = levenshteinDistance(previous, current);
      if (distance > maxEditDistance) {
        return false;
      }
      const relativeChange = distance / Math.max(previous.length, current.length);
      return relativeChange <= 0.2;
    },
    [levenshteinDistance]
  );

  return useCallback(
    async (
      actionGroup: ActionGroupFormValues,
      formikHelpers: FormikHelpers<ActionGroupFormValues>
    ) => {
      formikHelpers.setSubmitting(true);
      const invalidate = () => {
        queryClient.invalidateQueries([ENDPOINTS.SOLUTIONS]);
        queryClient.invalidateQueries([ENDPOINTS.ACTION_GROUPS]);
        queryClient.resetQueries([ENDPOINTS.ACTION_GROUPS]);
        queryClient.invalidateQueries([ENDPOINTS.ACTION_GROUPS_SKILLS]);
      };
      let actionGroupId = params.id;
      const updateScenario = !!actionGroupId && !!editGroupId;
      try {
        if (params.id) {
          const payload = new ActionGroupPayload({
            user: meData?.data.id,
            attachmentsType: "actionGroups",
            ...actionGroup,
          });
          const groupPayload = payload.getActionGroup();

          const groupAnalyticsPayload = {
            action_group_title: groupPayload?.title,
            action_group_goals: groupPayload?.description,
            action_group_schedule: groupPayload?.schedule,
            action_group_contact: groupPayload?.contact,
            action_group_skills: Object.assign(
              {},
              ...(actionGroup?.skills?.map((s, index) => {
                const splitCaps = (string: string) =>
                  string
                    .replace(/([a-z])([A-Z]+)/g, (m, s1, s2) => s1 + " " + s2)
                    .replace(
                      /([A-Z])([A-Z]+)([^a-zA-Z0-9]*)$/,
                      (m, s1, s2, s3) => s1 + s2 + s3
                    )
                    .replace(
                      /([A-Z]+)([A-Z][a-z])/g,
                      (m, s1, s2) => s1 + " " + s2
                    );
                const snakeCase = (string: string) =>
                  splitCaps(string)
                    .replace(/\W+/g, " ")
                    .split(/ |\B(?=[A-Z])/)
                    .map((word) => word)
                    .join("_");
                const skillKey = snakeCase(s.skill.label || `[${index}]`);
                return {
                  [skillKey]: Number(s?.timeRequired || 0),
                };
              }) || [])
            ),
            solution_title: solutionData?.data?.title,
            solution_details: solutionData?.data?.description,
            docs: actionGroup?.attachments
              ?.filter((a) => a.type === "file")
              ?.map((a) => (a as unknown as IAttachment<IAsset>)?.asset?.url),
            links: actionGroup?.attachments
              ?.filter((a) => a.type === "link")
              ?.map((a) => (a as unknown as IAttachment)?.data),
            labels: actionGroup?.tags,
          };
          const qualityCache = manage.qualityReviewRef?.current;

          const shouldEvaluateField = (field: FIELD_NAMES) => {
            if (!qualityCache) {
              return true;
            }
            const cacheKey = field.toLocaleLowerCase();
            const cached = qualityCache[cacheKey];
            if (!cached) {
              return true;
            }

            const isHighQuality =
              cached.quality === CONTENT_QUALITY_LEVELS.HIGH ||
              cached.quality === CONTENT_QUALITY_LEVELS.GOOD;
            if (!isHighQuality) {
              return true;
            }

            const currentValue = (actionGroup[field] as string) || "";
            const cachedNormalized = normalize(cached.user_input || "");
            const currentNormalized = normalize(currentValue);

            if (cachedNormalized === currentNormalized) {
              return false;
            }

            return !isMinorEdit(cachedNormalized, currentNormalized);
          };

          const fieldsToEvaluate = qualityTrackedFields.filter((field) =>
            shouldEvaluateField(field)
          );
          const fieldsToEvaluateSet = new Set(
            fieldsToEvaluate.map((field) => field.toLocaleLowerCase())
          );

          const requireAISuggestions =
            manage?.proceedWithSuggestions !== true &&
            fieldsToEvaluate.length > 0;

          let aiSuggestionsReviewResult: Pick<
            AxiosResponse<IAISuggestions>,
            "data"
          >;

          if (manage?.proceedWithSuggestions === true) {
            aiSuggestionsReviewResult = {
              data: {
                entity: "action_group",
                feedback_areas: [],
                more_feedback: {
                  quality: CONTENT_QUALITY_LEVELS.HIGH,
                  feedback: "",
                },
              },
            };
          } else if (!requireAISuggestions) {
            aiSuggestionsReviewResult = {
              data: {
                entity: "action_group",
                feedback_areas: qualityCache
                  ? Object.values(qualityCache)
                  : [],
                more_feedback: {
                  quality: CONTENT_QUALITY_LEVELS.HIGH,
                  feedback: "",
                },
              },
            };
          } else {
            const aiResponse = await mutateCreateAISuggestionGroupAsync(
              groupAnalyticsPayload
            );
            aiSuggestionsReviewResult = { data: aiResponse.data };
          }

          const feedbackAreas =
            aiSuggestionsReviewResult.data.feedback_areas || [];
          const trackedFeedbackAreas = feedbackAreas
            .map((area) => {
              const key = normaliseAiInputName(area.input_name);
              return {
                ...area,
                input_name: key,
              };
            })
            .filter((area) =>
              qualityTrackedFieldSet.has(area.input_name || "")
            ) as IFeedbackArea[];

          const baseCache: ActionGroupQualityReviewMap = qualityCache
            ? { ...qualityCache }
            : {};
          const nextQualityCache: ActionGroupQualityReviewMap = { ...baseCache };

          trackedFeedbackAreas.forEach((area) => {
            const key = (area.input_name || "").toLocaleLowerCase();
            if (fieldsToEvaluateSet.has(key) || !nextQualityCache[key]) {
              nextQualityCache[key] = area;
            }
          });

          const mergedFeedbackAreas = qualityTrackedFields
            .map((field) => {
              const key = field.toLocaleLowerCase();
              return nextQualityCache[key];
            })
            .filter((area): area is NonNullable<typeof area> => !!area);

          if (manage.qualityReviewRef) {
            manage.qualityReviewRef.current = nextQualityCache;
          }

          const sanitizedResult: IAISuggestions = {
            ...aiSuggestionsReviewResult.data,
            feedback_areas:
              mergedFeedbackAreas.length > 0
                ? mergedFeedbackAreas
                : trackedFeedbackAreas,
          };

          formikHelpers.setFieldValue("suggestedResult", sanitizedResult);

          if (
            manage?.proceedWithSuggestions === false &&
            ((sanitizedResult.feedback_areas.length > 0 &&
              sanitizedResult.feedback_areas.find(
                (area) =>
                  area.quality !== CONTENT_QUALITY_LEVELS.GOOD &&
                  area.quality !== CONTENT_QUALITY_LEVELS.HIGH
              )) ||
              sanitizedResult.more_feedback?.quality === "Useless" ||
              sanitizedResult.more_feedback?.quality === "Poor")
          ) {
            formikHelpers.setTouched({
              title: false,
              description: false,
              schedule: false,
              contact: false,
            });
            document.querySelector("#group-report")?.scrollTo(0, 0);
            formikHelpers.setSubmitting(false);
            return;
          }

          let aiSimilarSolutionsResult: {
            data: { potential_similarity_id: string[] };
          };
          if (manage?.proceedWithSimilar === true) {
            aiSimilarSolutionsResult = {
              data: { potential_similarity_id: [] },
            };
          } else {
            try {
              const response = await mutateCreateAISimilarGroupAsync(
                groupAnalyticsPayload
              );
              aiSimilarSolutionsResult = { data: response.data };
            } catch (similarError) {
              console.warn(
                "Action group similarity check failed, continuing without blocking",
                similarError
              );
              aiSimilarSolutionsResult = {
                data: { potential_similarity_id: [] },
              };
            }
          }

          if (
            !updateScenario &&
            manage?.proceedWithSimilar === false &&
            aiSimilarSolutionsResult?.data?.potential_similarity_id.length > 0
          ) {
            return;
          }

          if (editGroupId) {
            if (manage.showIssueFields) {
              const updated = await mutateUpdateActionGroupAsync(
                payload.getActionGroup()
              );
              actionGroupId = updated.data.id;
              showSuccessMessage("Action group is updated!");
            }
            await mutateUpdateActionGroupSkillAsync({
              actionGroup: actionGroupForEdit?.data?.id,
              skills: payload.getSkills(),
            });
            showSuccessMessage("Action group skills are updated!");
          } else {
            if (manage.showIssueFields) {
              const created = await mutateCreateActionGroupAsync({
                ...payload.getActionGroup(),
                solutions: isOnActionGroupPage
                  ? payload.solutions
                  : [params.id],
              });
              actionGroupId = created.data.id;
              showSuccessMessage("Action group is created!");
              if (payload.getSkills().length) {
                await mutateCreateActionGroupSkillAsync({
                  actionGroup: created.data.id,
                  skills: payload.getSkills(),
                });
                showSuccessMessage("Action group skills are created!");
              }
            }
          }
          if (actionGroupId) {
            const attachmentsPayload = payload.getAttachments(actionGroupId);
            if (attachmentsPayload && attachmentsPayload.attachments?.length) {
              await updateAttachments(attachmentsPayload);
              showSuccessMessage("Attachments are updated!");
            }
          }
          success();
          invalidate();
          queryClient.getMutationCache().clear();
          formikHelpers.setFieldValue("suggestedResult", undefined);
          if (manage.qualityReviewRef) {
            manage.qualityReviewRef.current = {};
          }
        } else {
          formikHelpers.setSubmitting(false);
          showErrorMessage("No solution occurred! Check page!");
        }
      } catch (error: any) {
        formikHelpers.setSubmitting(false);
        if (error?.response?.data) {
          formikHelpers.setErrors(error.response.data);
        }
        showErrorMessage("Error occurred! Check form!");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      actionGroupForEdit?.data?.id,
      editGroupId,
      isMinorEdit,
      isOnActionGroupPage,
      manage?.proceedWithSimilar,
      manage?.proceedWithSuggestions,
      manage?.qualityReviewRef,
      manage.showIssueFields,
      meData?.data.id,
      mutateCreateAISimilarGroupAsync,
      mutateCreateAISuggestionGroupAsync,
      mutateCreateActionGroupAsync,
      mutateCreateActionGroupSkillAsync,
      mutateUpdateActionGroupAsync,
      mutateUpdateActionGroupSkillAsync,
      normalize,
      params.id,
      queryClient,
      qualityTrackedFieldSet,
      qualityTrackedFields,
      solutionData?.data?.description,
      solutionData?.data?.title,
      success,
      updateAttachments,
    ]
  );
}
